// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awOrange } from 'Utils/themes';
import { resolveProjectStatus, ProjectStatusKind } from 'Utils/projectStatus';
import { nameProjectProgressStep } from 'Utils/projectProgress';

export type PinStatusKind = ProjectStatusKind;

export interface IPinStatusChip {
  label: string;
  kind: PinStatusKind;
}

// The chip half of the shared status reading - the grid card takes the same answer and renders
// its step instead. Kept as a named function because the map and rail want a chip, not a status.
export const getPinStatusChip = (project: { orderStatus?: number; isEstimate?: boolean }): IPinStatusChip => {
  const { label, kind } = resolveProjectStatus(project);
  return { label, kind };
};

// The chip with the pipeline stage named alongside it: "In Progress - QC". For the surfaces
// that draw no pipeline of their own - the rail's cards and the pin card - so the stage is still
// readable there; the grid card draws its stepper and takes the plain chip above instead.
// Only an in-progress project has a stage: a delivered or canceled one has left the pipeline,
// and naming a stage for it would describe work that is no longer happening.
export const getStatusChipWithPhase = (project: { orderStatus?: number; isEstimate?: boolean }): IPinStatusChip => {
  const { label, kind, step } = resolveProjectStatus(project);
  const phase = kind === 'inProgress' ? nameProjectProgressStep(step) : null;
  return { label: phase ? `${label} - ${phase}` : label, kind };
};

// One dot-color source for every surface that renders a pin status (rail cards, map popup):
// the Figma pairs "Completed" with a green dot; in-progress reads accent blue.
export const getStatusDotColor = (theme: any, kind: PinStatusKind): string => {
  const dotColors: Record<PinStatusKind, string> = {
    delivered: theme.palette.success.main,
    inProgress: theme.palette.primary.main,
    estimate: awOrange,
    canceled: theme.palette.darkGrey,
    notSubmitted: theme.palette.darkGrey,
  };
  return dotColors[kind];
};
